import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { BuildingModel } from '@/api/models/building';
import { EmailTemplateMtModel } from '@/api/models/emailTemplateMt';
import { PropertyLocationWithEmailAndPhoneModel } from '@/api/models/propertyLocation';
import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export type ApiResponse_GetAllEmailTemplates_Building = Array<
  Omit<EmailTemplateMtModel, 'created_at' | 'updated_at'> & {
    location: Pick<PropertyLocationWithEmailAndPhoneModel, 'name' | 'email' | 'phone'>;
  }
>;

const METHOD = 'GET';
const ENDPOINT = 'building/:id/email-templates';
export const QUERY_KEY = (id: BuildingModel['id']) => getQueryKey(METHOD, ENDPOINT, { id });

export const useGetAllEmailTemplates = (buildingId: BuildingModel['id'], enabled = true) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!session?.access_token,
    queryKey: QUERY_KEY(buildingId),
    queryFn: async () => {
      try {
        const data = await handleApiCall<ApiResponse_GetAllEmailTemplates_Building>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            id: buildingId.toString(),
          })
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch email templates.', { variant: 'error' });

        // eslint-disable-next-line no-console
        console.error(err);

        throw err;
      }
    },
  });
};
